import { useEffect, type ReactNode } from "react";
import type { IMultiplacerModuleKey } from "@multiconsult-group/multiplacer-core";
import { Navigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectAvailableModulesForCurrentUserInCurrentProject,
  selectIsCurrentUserAccessReady,
  selectVisibleModuleKeysForCurrentProject,
} from "../projectSelectors";
import { useInternalMsalAccount } from "../hooks/useInternalMsalAccount";
import { changeActiveModuleThunk } from "../DesignTab/designThunk";
import {
  MODULE_PAGES,
  modulePageUrl,
  readProjectModule,
  type ModulePage,
} from "./moduleNavigation";

export function ModuleRoute({
  projectNo,
  page,
  children,
  moduleOverride,
}: {
  projectNo: number;
  page: ModulePage;
  children: ReactNode;
  moduleOverride?: IMultiplacerModuleKey;
}) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const module = moduleOverride ?? readProjectModule(location.search);
  const active = useAppSelector((state) => state.design.activeModule);
  const visible = useAppSelector(selectVisibleModuleKeysForCurrentProject);
  const available = useAppSelector(selectAvailableModulesForCurrentUserInCurrentProject);
  const ready = useAppSelector(selectIsCurrentUserAccessReady);
  const internal = useInternalMsalAccount();
  const allowed =
    module != null && visible.includes(module) && (internal || available.includes(module));
  useEffect(() => {
    if ((ready || internal) && allowed && module !== active)
      dispatch(changeActiveModuleThunk(module));
  }, [ready, internal, allowed, module, active, dispatch]);
  if (!ready && !internal) return <div>Laster tilgang…</div>;
  if (!module || !allowed) return <Navigate to={`/${projectNo}/modules`} replace />;
  if (!MODULE_PAGES[module].includes(page))
    return <Navigate to={modulePageUrl(projectNo, module)} replace />;
  if (active !== module) return <div>Laster modul…</div>;
  return <>{children}</>;
}
